import { Column } from '@access-erp/common';
import { CellType, ICellRendererParams, Tooltip, ValueFormatterParams } from '@access-erp/ui-components';

import { EditLined, PauseLined, HourglassFullFilled } from '@tag/tag-icons';

import { Pill } from '@/components/common/Pill/Pill';

import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { guidFilterOptions } from '@/components/common/EnquiriesScreen/utils/filters.config';
import { getCustomerLabel } from './helpers';
import { EnvUtils } from '@/utils/env.utils';
import { NOT_AVAILABLE_CHAR } from '@/config/common.config';
import { lastNextDayGap, dateCellRenderer } from '@/pages/ContractDetails/ContractLines/grid/helper';
import { 
  ContractBillingStatusPascalCaseEnum,
  ContractStatusEnum 
} from '@/graphql/contracts/_generated_/contracts';

const VISIBLE_REASON_CHARS_LENGTH = 50;

const baseColumnConfig = {
  sortable: false,
  suppressAdvancedFilter: true,
};

export const getBcLineTypeColumns: Column<any>[] = [
  {
    name: 'id',
    caption: 'Contract Line Id',
    width: 180,
    cellType: CellType.String,
    hidden: true,
    ...baseColumnConfig,
    filterOptions: {
      availableOptions: guidFilterOptions,
    },
  },
  {
    name: 'status',
    caption: 'Status',
    width: 180,
    cellType: CellType.String,
    ...baseColumnConfig,
  },
  {
    name: 'lineBillingStatus',
    caption: 'Pause Status',
    width: 150,
    cellType: CellType.String,
    ...baseColumnConfig,
    cellStyle: { justifyContent: 'center', display: 'flex' },
    cellRenderer: ({
      value,
      data,
    }: ICellRendererParams<any, string>) => {
      if (
        data?.status !== ContractStatusEnum.Active &&
        data?.status !== ContractStatusEnum.ActiveDNR &&
        data?.status !== ContractStatusEnum.Terminating
      ) {
        return null;
      }

      const status = value as ContractBillingStatusPascalCaseEnum;

      if (status === ContractBillingStatusPascalCaseEnum.Paused) {
        const startDate = data?.lineBillingCurrentStatusStartDate
          ? new Date(data.lineBillingCurrentStatusStartDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          : 'N/A';
        const endDate = data?.lineBillingCurrentStatusEndDate
          ? new Date(data.lineBillingCurrentStatusEndDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          : 'N/A';
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip
              tooltipFor={
                <span>
                  <Pill
                    background="#9C27B0"
                    size="large"
                    style={{ cursor: 'default', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    Paused <PauseLined style={{ fill: 'white', height: '20px' }} />
                  </Pill>
                </span>
              }
            >
              Paused
              <br />
              Start Date: {startDate}
              <br />
              End Date: {endDate}
            </Tooltip>
          </div>
        );
      }

      if (status === ContractBillingStatusPascalCaseEnum.FuturePause) {
        const startDate = data?.lineBillingCurrentStatusStartDate
          ? new Date(data.lineBillingCurrentStatusStartDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          : 'N/A';
        const endDate = data?.lineBillingCurrentStatusEndDate
          ? new Date(data.lineBillingCurrentStatusEndDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
          : 'N/A';
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip
              tooltipFor={
                <span>
                  <Pill
                    background="#00BCD4"
                    size="large"
                    style={{ cursor: 'default', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    Future Pause <HourglassFullFilled style={{ fill: 'white', height: '20px' }} />
                  </Pill>
                </span>
              }
            >
              Future Pause
              <br />
              Start Date: {startDate}
              <br />
              End Date: {endDate}
            </Tooltip>
          </div>
        );
      }

      if (status === ContractBillingStatusPascalCaseEnum.On) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip
              tooltipFor={
                <span>
                  <Pill
                    background="#2196F3"
                    size="large"
                    style={{ cursor: 'default', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    On <EditLined style={{ fill: 'white', height: '20px' }} />
                  </Pill>
                </span>
              }
            >
              On
            </Tooltip>
          </div>
        );
      }

      return null;
    },
  },
  {
    name: 'stockCode',
    caption: 'Stock Code',
    width: 200,
    cellType: CellType.String,
    ...baseColumnConfig,
  },
  {
    name: 'quantity',
    caption: 'Quantity',
    width: 160,
    cellType: CellType.Decimal,
    cellFormatter: CellFormatters.integer,
    ...baseColumnConfig,
  },
  {
    name: 'unitType',
    caption: 'Unit Type',
    width: 200,
    cellType: CellType.String,
    ...baseColumnConfig,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'unitPrice',
    caption: 'Unit Price',
    width: 170,
    cellType: CellType.String,
    cellFormatter: CellFormatters.decimal,
    ...baseColumnConfig,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'unitPriceFrequency',
    caption: 'Unit Price Frequency',
    width: 150,
    cellType: CellType.String,
    valuePath: 'unitPriceFrequency',
    ...baseColumnConfig,
  },
  ...(!EnvUtils.isProRataFirstPeriodEnabled()
    ? [
      {
        name: 'lineStartDate',
        caption: 'Line Start Date',
        width: 180,
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        ...baseColumnConfig,
        hidden: true,
      },
    ]
    : []),
  {
    name: 'billingType',
    caption: 'Billing Type',
    width: 160,
    cellType: CellType.String,
    ...baseColumnConfig,
  },
  {
    name: 'billingInterval',
    caption: 'Billing Interval',
    width: 160,
    cellType: CellType.String,
    ...baseColumnConfig,
  },
  {
    name: 'nextInvoiceDate',
    caption: 'Next Invoice Date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    ...baseColumnConfig,
  },
  ...(EnvUtils.isNextSoIssueDateEnabled() ? [{
    name: 'nextSoIssueDate',
    caption: 'Next SO Issue Date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    ...baseColumnConfig,
  }] : []),
  {
    name: 'salesHandoverNumber',
    caption: 'Sales Handover Number',
    width: 180,
    cellType: CellType.String,
    ...baseColumnConfig,
  },
  {
    name: 'nextBillingStartDate',
    caption: 'Next Billing Start Date',
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    cellRenderer: ({
      value,
      data,
    }: ICellRendererParams<any, string>) => {
              
      let displayValue = '';
              
      if (!value && data?.nextBillingStartDate == null) {
        displayValue = NOT_AVAILABLE_CHAR;
      }
              
      let dayGap = lastNextDayGap(data);
      displayValue = dateCellRenderer({ value });
              
      return (<>
                      {(dayGap > 1) && 
                      <Tooltip tooltipFor={<span>{'\u26A0\uFE0F'}</span>}>
                        There is a {dayGap}-day gap between the last and next billed periods.
                      </Tooltip>}
                      {displayValue}
                      </>);
    },
    minWidth: 180,
    ...baseColumnConfig,
  },
  {
    name: 'nextBillingEndDate',
    caption: 'Next Billing End Date',
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    cellRenderer: ({
      value,
      data,
    }: ICellRendererParams<any, string>) => {
        
      let displayValue = '';
        
      if (!value && data?.nextBillingEndDate == null) {
        return NOT_AVAILABLE_CHAR;
      }
        
      let dayGap = lastNextDayGap(data);
      displayValue = dateCellRenderer({ value });
        
      return (<>
                {(dayGap > 1) && 
                <Tooltip tooltipFor={<span>{'\u26A0\uFE0F'}</span>}>
                  There is a {dayGap}-day gap between the last and next billed periods.
                </Tooltip>}
                {displayValue}
                </>);
    },
    minWidth: 180,
    ...baseColumnConfig,
  },
  ...(EnvUtils.isProRataFirstPeriodEnabled()
    ? [
      {
        name: 'proRataEndDate',
        caption: 'Pro Rata End Date',
        cellType: CellType.Date,
        cellFormatter: CellFormatters.date,
        minWidth: 180,
        ...baseColumnConfig,
      },
    ]
    : []),
  {
    name: 'origin',
    caption: 'Origin',
    width: 130,
    cellType: CellType.String,
    ...baseColumnConfig,
  },
];

export const variableTypeColumns = [
  {
    name: 'minCharge',
    caption: 'Min Charge',
    width: 160,
    cellType: CellType.Decimal,
    cellFormatter: (params: ValueFormatterParams<any, any>) => CellFormatters.decimal(params),
    ...baseColumnConfig,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'lastSOQuantity',
    caption: 'Last SO Quantity',
    cellType: CellType.Integer,
    cellFormatter: CellFormatters.integer,
    width: 180,
    ...baseColumnConfig,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'lastSOTotalValue',
    caption: 'Last SO Total Value',
    cellType: CellType.String,
    cellFormatter: (params: ValueFormatterParams<any, any>) => CellFormatters.decimal(params),
    width: 180,
    ...baseColumnConfig,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'lastSOStartDate',
    caption: 'Last SO Start Date',
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    minWidth: 180,
    ...baseColumnConfig,
  },
  {
    name: 'lastSOEndDate',
    caption: 'Last SO End Date',
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    minWidth: 180,
    ...baseColumnConfig,
  },
];

export const purchaseOrderColumns: Column<unknown>[] = [
  {
    name: 'poNumber',
    caption: 'PO Number',
    minWidth: 180,
    cellType: CellType.String,
    ...baseColumnConfig,
  },
  {
    name: 'poTotalValue',
    caption: 'PO Total Value',
    minWidth: 180,
    cellType: CellType.Decimal,
    cellFormatter: (params: ValueFormatterParams) => CellFormatters.decimal(params),
    ...baseColumnConfig,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'poRemainingValue',
    caption: 'PO Remaining Value',
    minWidth: 180,
    cellType: CellType.Decimal,
    cellFormatter: (params: ValueFormatterParams) => CellFormatters.decimal(params),
    ...baseColumnConfig,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'poDate',
    caption: 'PO Date',
    minWidth: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    ...baseColumnConfig,
  },
  {
    name: 'poStartDate',
    caption: 'PO Start Date',
    minWidth: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    ...baseColumnConfig,
  },
  {
    name: 'poEndDate',
    caption: 'PO End Date',
    minWidth: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    ...baseColumnConfig,
  },
  {
    name: 'poStatus',
    caption: 'PO Status',
    minWidth: 180,
    cellType: CellType.String,
    ...baseColumnConfig,
    cellRenderer: ({ value }: ICellRendererParams) =>
      !!value && value !== 'Active' ? <span>{'\u26A0\uFE0F ' + value}</span> : <span>{value}</span>,
  },
  {
    name: 'poNote',
    caption: 'PO Note',
    width: 250,
    cellType: CellType.String,
    ...baseColumnConfig,
    cellRenderer: ({ value }: ICellRendererParams) => {
      const reasonForFailureTrimmed =
        value && value.length > VISIBLE_REASON_CHARS_LENGTH
          ? value.slice(0, VISIBLE_REASON_CHARS_LENGTH) + '...'
          : value || '';

      const titlePopUp = value && value.length > VISIBLE_REASON_CHARS_LENGTH ? value : undefined;
      return <div title={titlePopUp}>{reasonForFailureTrimmed}</div>;
    },
  },
];

export const shipToInvoiceToColumns = [
  {
    name: 'shipToCustomer',
    caption: 'Ship To Account',
    width: 280,
    cellType: CellType.String,
    cellRenderer: (params: ICellRendererParams) => {
      return getCustomerLabel(
        params.data?.shipToCustomerAccountNumber,
        params.data?.shipToCustomerName,
        params.data?.shipToCustomerPostcode,
      );
    },
  },
  {
    name: 'invoiceToCustomer',
    caption: 'Invoice To Account',
    width: 280,
    cellType: CellType.String,
    cellRenderer: (params: ICellRendererParams) => {
      return getCustomerLabel(
        params.data?.invoiceToCustomerAccountNumber,
        params.data?.invoiceToCustomerName,
        params.data?.invoiceToCustomerPostcode,
      );
    },
  },
];

export const oppColumns = [
  {
    name: 'oppHeaderId',
    caption: 'Opp Header Id',
    width: 180,
    cellType: CellType.String,
  },
  {
    name: 'oppLineId',
    caption: 'Opp Line Id',
    width: 180,
    cellType: CellType.String,
  },
];
